import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { systemBus, EVENTS } from '../services/systemBus';
import MobileModal from './MobileModal';

const VaultDialog = ({
  showVaultDialog,
  setShowVaultDialog,
  setVaults
}) => {
  // [UI-MOBILEMODAL-ADOPTION wave 1]: MobileModal registers useModalEscape
  // internally (avoid double-registration); handleClose is passed through
  // as its onClose.
  const handleClose = useCallback(() => setShowVaultDialog(false), [setShowVaultDialog]);

  const [vaultName, setVaultName] = useState('');
  const [vaultLocation, setVaultLocation] = useState('');
  const [vaultCost, setVaultCost] = useState('');

  // [PHASE 5 SESSION 4]: Force re-render mechanism for reactive vault creation
  // [PURPOSE]: Enables dialog to react to vault creation and currency changes
  // [WHY]: When vaults are created elsewhere or currency changes, this dialog should update
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 5 SESSION 4]: Event subscriptions for reactive vault creation dialog
  // [PURPOSE]: Subscribe to vault and currency events for real-time feedback
  // [WHY]: This dialog needs to show real-time updates when:
  //   - Vaults are created from other sources (party sync, imports)
  //   - Currency changes (affects affordability of vault access cost)
  //   - Vault list updates (might affect naming conflicts)
  // [INTEGRATION]: Provides immediate feedback about vault creation viability
  useEffect(() => {
    const handleVaultEvent = (eventName, data) => {
      // Force re-render to update form state or show feedback
      forceUpdate();
    };

    // [VAULT_CREATED]: New vault created (from any source)
    const unsubscribeCreated = systemBus.on(EVENTS.VAULT_CREATED, (data) =>
      handleVaultEvent('VAULT_CREATED', data));

    // [CURRENCY_CHANGED]: Currency changed (affects access cost affordability)
    const unsubscribeCurrency = systemBus.on(EVENTS.CURRENCY_CHANGED, (data) =>
      handleVaultEvent('CURRENCY_CHANGED', data));

    return () => {
      unsubscribeCreated();
      unsubscribeCurrency();
    };
  }, []);
  
  const handleSubmit = () => {
    const newVault = {
      id: `vault_${Date.now()}`,
      name: vaultName || 'Storage Vault',
      location: vaultLocation || 'Town Bank',
      cost: vaultCost ? parseFloat(vaultCost) : 0,
      items: []
    };
    
    setVaults(prev => [...prev, newVault]);
    setShowVaultDialog(false);
    setVaultName('');
    setVaultLocation('');
    setVaultCost('');
  };
  
  return (
    <MobileModal
      isOpen={showVaultDialog}
      onClose={handleClose}
      title="Create New Vault"
      showSaveButton={false}
    >
      <div className="mb-4">
        <label className="block text-sm text-gray-300 mb-1">Vault Name</label>
        <input
          type="text"
          value={vaultName}
          onChange={(e) => setVaultName(e.target.value)}
          placeholder="Storage Vault"
          className="w-full p-2 bg-gray-800 text-white rounded"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm text-gray-300 mb-1">Location</label>
        <input
          type="text"
          value={vaultLocation}
          onChange={(e) => setVaultLocation(e.target.value)}
          placeholder="Town Bank"
          className="w-full p-2 bg-gray-800 text-white rounded"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm text-gray-300 mb-1">Access Cost (gp)</label>
        <input
          type="number"
          step="0.01"
          value={vaultCost}
          onChange={(e) => setVaultCost(e.target.value)}
          placeholder="0"
          className="w-full p-2 bg-gray-800 text-white rounded"
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          className="flex-1 bg-green-700 hover:bg-green-600 text-white p-2 rounded transition-colors"
        >
          Create Vault
        </button>
        <button
          onClick={() => setShowVaultDialog(false)}
          className="flex-1 bg-red-700 hover:bg-red-600 text-white p-2 rounded transition-colors"
        >
          Cancel
        </button>
      </div>
    </MobileModal>
  );
};

export default VaultDialog;